import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import Button from '../../components/base/Button';

export default function About() {
  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Clinical Excellence',
      description: 'Hospital-grade safety protocols and pharmaceutical-grade standards maintained throughout every aspect of patient care and infusion administration'
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Patient-Centered Care',
      description: 'Private infusion suites designed to reduce anxiety and enhance therapeutic experience while maintaining uncompromising clinical standards'
    },
    {
      icon: 'ri-team-line',
      title: 'Physician Collaboration',
      description: 'Seamless communication and coordination with referring specialists to ensure optimal treatment outcomes and continuity of care'
    },
    {
      icon: 'ri-time-line',
      title: 'Expedited Access',
      description: 'Same-week scheduling and accelerated authorization processing to minimize delays in medically necessary specialty infusion therapy'
    }
  ];

  const expertise = [
    {
      icon: 'ri-microscope-line',
      title: 'Evidence-Based Clinical Protocols',
      description: 'Medically supervised infusion protocols with continuous monitoring and immediate physician access for optimal safety and therapeutic efficacy'
    },
    {
      icon: 'ri-heart-pulse-line',
      title: 'Critical-Care Trained Staff',
      description: 'Expert infusion specialists with extensive experience in high-acuity infusion management and adverse reaction intervention protocols'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Insurance Authorization Expertise',
      description: 'Specialized benefits investigation team managing complex prior authorizations, appeals, and comprehensive insurance coordination'
    },
    {
      icon: 'ri-building-line',
      title: 'State-of-the-Art Facility',
      description: 'Advanced outpatient infusion center with private treatment suites, precision monitoring equipment, and pharmaceutical-grade safety systems'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] text-white py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="max-w-4xl">
              <h1 className="text-6xl font-bold mb-8">About Zeus Specialty Infusion Clinic</h1>
              <p className="text-2xl text-gray-100 leading-relaxed font-light">
                Advanced outpatient infusion therapy delivered with precision, clinical expertise, and unwavering commitment to patient safety and therapeutic excellence.
              </p>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <img
                  src="https://public.readdy.ai/ai/img_res/f14a2173a10bf62b9916653f9c9bdcec.jpg"
                  alt="Zeus Specialty Infusion Clinic facility"
                  className="rounded-3xl shadow-2xl w-full h-[600px] object-cover object-top"
                />
              </div>
              <div>
                <h2 className="text-5xl font-bold text-[#0f1e32] mb-8">Our Mission</h2>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                  Zeus Specialty Infusion Clinic was established to address the critical need for high-quality, accessible outpatient infusion therapy in Los Angeles. We provide hospital-grade clinical protocols in private infusion suites that prioritize patient safety, therapeutic efficacy, and seamless coordination with referring physicians.
                </p>
                <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                  Our facility specializes in complex specialty infusions including IVIG therapy, iron replacement, biologic medications, and physician-directed specialty protocols. Every treatment is administered by critical-care trained infusion specialists with continuous medical oversight and immediate intervention capability.
                </p>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We eliminate administrative barriers through comprehensive insurance coordination and expedited authorization processing, ensuring patients receive medically necessary therapy without delay while maintaining the highest standards of clinical excellence and patient-centered care.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-[#f8f9fa] to-[#e9ecef]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-[#0f1e32] mb-6">Our Core Values</h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Zeus Specialty Infusion Clinic operates with unwavering commitment to clinical excellence, patient safety, and collaborative physician partnerships.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border border-gray-200">
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center mb-6 shadow-md">
                    <i className={`${value.icon} text-4xl text-[#d4af37]`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#0f1e32] mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-20">
              <h2 className="text-5xl font-bold text-[#0f1e32] mb-6">Clinical Expertise & Capabilities</h2>
              <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                Our facility combines advanced medical technology, expert clinical staff, and comprehensive administrative support to deliver superior specialty infusion care throughout Los Angeles.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {expertise.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-10 shadow-lg hover:shadow-2xl transition-all border border-gray-200">
                  <div className="flex items-start space-x-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                      <i className={`${item.icon} text-4xl text-[#d4af37]`}></i>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#0f1e32] mb-4">{item.title}</h3>
                      <p className="text-gray-600 text-lg leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-24 bg-gradient-to-br from-[#0f1e32] to-[#1e3a5f] text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-5xl font-bold mb-8">Clinical Leadership & Direction</h2>
                <p className="text-2xl text-gray-100 mb-6 leading-relaxed font-light">
                  Zeus Specialty Infusion Clinic is led by experienced healthcare professionals with extensive backgrounds in critical-care medicine, advanced infusion therapy, and outpatient specialty care management.
                </p>
                <p className="text-xl text-gray-200 mb-6 leading-relaxed">
                  Our clinical leadership team brings decades of combined experience in high-acuity infusion management, having trained and practiced in premier medical institutions. This expertise ensures every patient receives the highest level of clinical care with meticulous attention to safety protocols and therapeutic outcomes.
                </p>
                <p className="text-xl text-gray-200 leading-relaxed">
                  The founding vision was to create an outpatient infusion center that combines hospital-level clinical excellence with the comfort, convenience, and personalized attention of a private practice—eliminating the institutional environment of traditional infusion centers while maintaining uncompromising safety standards and medical oversight.
                </p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20medical%20leadership%20team%20in%20luxury%20clinical%20office%2C%20sophisticated%20healthcare%20executives%20in%20modern%20medical%20facility%2C%20pristine%20white%20office%20with%20navy%20blue%20and%20gold%20accents%2C%20state-of-the-art%20medical%20center%20interior%2C%20professional%20healthcare%20leadership%20photography%2C%20elite%20medical%20practice%20environment%2C%20advanced%20clinical%20workspace%20with%20natural%20lighting&width=700&height=600&seq=leadership-elite-002&orientation=landscape"
                  alt="Clinical leadership at Zeus Infusion"
                  className="rounded-3xl shadow-2xl w-full h-[600px] object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-gradient-to-br from-[#f8f9fa] to-white rounded-3xl p-16 text-center border border-gray-200 shadow-xl">
              <h2 className="text-5xl font-bold text-[#0f1e32] mb-8">Experience Advanced Infusion Care</h2>
              <p className="text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
                Whether you're a physician seeking a trusted specialty infusion partner or a patient requiring expert infusion therapy, Zeus Specialty Infusion Clinic delivers uncompromising clinical excellence with comprehensive support throughout Los Angeles.
              </p>
              <div className="flex flex-wrap justify-center gap-5">
                <Link to="/physicians">
                  <Button variant="primary" size="lg" className="!px-10 !py-4 !text-lg">
                    <i className="ri-hospital-line mr-2"></i>
                    Physician Referral Information
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="secondary" size="lg" className="!px-10 !py-4 !text-lg">
                    <i className="ri-phone-line mr-2"></i>
                    Contact Our Team
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
